import { graphApi, type PatternQueryRequest } from '@/services/api/graph'
import type { ActiveGraphFilter } from '@/types/graph-query'
import type { GraphView, GraphViewEdge, GraphViewNode, GraphViewState } from '@/types/graph-view'
import { emptyViewState } from '@/types/graph-view'

/** Build a patternQuery request body from a single ActiveGraphFilter */
export function buildPatternBody(filter: ActiveGraphFilter): PatternQueryRequest {
  const f = filter as any
  const body: any = { limit: 500 }
  if (f.searchTerm && !f.origin) {
    body.search_term = f.searchTerm
  } else if (f.origin) {
    body.origin = {
      entity_type: f.origin.entityType,
      search_term: f.origin.searchTerm || null,
      element_id: f.origin.elementId || null,
    }
    body.chain = (f.chain || []).map((s: any) => ({
      relationship_type: s.relationshipType || null,
      target: {
        entity_type: s.target.entityType,
        search_term: s.target.searchTerm || null,
        element_id: s.target.elementId || null,
      },
    }))
  } else if (f.componentId != null) {
    body.component_id = f.componentId
  }
  return body
}

interface UnionResult {
  nodes: any[]
  relationships: any[]
  total: number
}

/**
 * Run a set of pattern query filters in parallel and combine the results via
 * UNION. Nodes are deduped by id across all result sets; edges are deduped by
 * id and then restricted to those whose both endpoints survived the union.
 *
 * Internal helper — returns the raw backend shape. Public consumers use either
 * `fetchPatternUnion` (new canonical `GraphView`) or `fetchPatternUnionLegacy`
 * (backward-compat shape for existing consumers pending migration).
 */
async function fetchUnionRaw(caseId: string, filters: ActiveGraphFilter[]): Promise<UnionResult> {
  if (filters.length === 0) {
    return { nodes: [], relationships: [], total: 0 }
  }

  const results = await Promise.all(
    filters.map(f => graphApi.patternQuery(caseId, buildPatternBody(f)))
  )

  if (results.length === 1) {
    const r = results[0]
    return {
      nodes: r.nodes || [],
      relationships: r.relationships || [],
      total: r.total ?? (r.nodes?.length || 0),
    }
  }

  const nodesById = new Map<string, any>()
  for (const r of results) {
    for (const n of r.nodes || []) {
      if (!nodesById.has(n.id)) nodesById.set(n.id, n)
    }
  }
  const unionNodes = [...nodesById.values()]

  // Each individual patternQuery only returns edges between its own result set,
  // so cross-type edges (e.g. BTSStation -> Location when the two came from
  // different filters) are missing. Ask the backend for all edges between the
  // union's nodes — Bloom-style "scene" semantics.
  const nodeIds = [...nodesById.keys()]
  let unionEdges: any[] = []
  if (nodeIds.length > 0) {
    try {
      const res = await graphApi.getEdgesBetween(caseId, nodeIds)
      unionEdges = res.relationships || []
    } catch (err) {
      console.error('edges-between fetch failed, falling back to per-query edges', err)
      const edgesById = new Map<string, any>()
      for (const r of results) {
        for (const e of r.relationships || []) {
          if (!edgesById.has(e.id)) edgesById.set(e.id, e)
        }
      }
      unionEdges = [...edgesById.values()].filter(
        e => nodesById.has(e.source) && nodesById.has(e.target)
      )
    }
  }

  return { nodes: unionNodes, relationships: unionEdges, total: unionNodes.length }
}

function toEnvelopeNode(n: any): GraphViewNode {
  const props = (n.properties && typeof n.properties === 'object' ? n.properties : {}) as Record<
    string,
    unknown
  >
  // The backend includes top-level fields (id, name, labels) plus a properties
  // bag. Snapshot the bag verbatim so the envelope is self-contained — the
  // GraphView is the source of truth for evidence/save.
  return {
    id: String(n.id),
    labels: Array.isArray(n.labels) ? [...n.labels] : [],
    typeLabel: n.typeLabel ?? n.type_label ?? n.labels?.[0],
    name: n.name ?? undefined,
    properties: { ...props },
  }
}

function toEnvelopeEdge(e: any): GraphViewEdge {
  const props = (e.properties && typeof e.properties === 'object' ? e.properties : {}) as Record<
    string,
    unknown
  >
  return {
    id: String(e.id),
    type: String(e.type ?? ''),
    source: String(e.source),
    target: String(e.target),
    properties: { ...props },
  }
}

/**
 * Run a set of chip filters and return a canonical `GraphView`. Carries the
 * caller-supplied view state (positions, hides) into the envelope so user
 * edits survive refetches.
 *
 * This is the canonical contract — partner with the Pydantic `GraphView` in
 * `src/libs/services/spectre/services/graph_view.py`.
 *
 * Existing consumers that have not yet been migrated to read `view.nodes` /
 * `view.edges` should use {@link fetchPatternUnionLegacy} for now.
 */
export async function fetchPatternUnion(
  caseId: string,
  filters: ActiveGraphFilter[],
  carry: GraphViewState = emptyViewState()
): Promise<GraphView> {
  const raw = await fetchUnionRaw(caseId, filters)
  return {
    meta: {
      source: 'chips',
      caseId,
      createdAt: new Date().toISOString(),
      label:
        filters.length === 0
          ? 'No filters'
          : `Filtered by ${filters.length} chip${filters.length === 1 ? '' : 's'}`,
    },
    nodes: raw.nodes.map(toEnvelopeNode),
    edges: raw.relationships.map(toEnvelopeEdge),
    view: { ...emptyViewState(), ...carry },
  }
}

/**
 * Backward-compatible adapter that returns the legacy `{nodes, relationships,
 * total}` shape. Used by existing consumers (`KnowledgeGraphView`,
 * `GeolocationView`, `AllDataView`) until each is migrated to read
 * `GraphView` directly.
 *
 * This adapter exists so the canonical `GraphView` contract can land
 * without a flag-day refactor of every consumer. Once consumers migrate, this
 * function and its callers should be removed.
 */
export async function fetchPatternUnionLegacy(
  caseId: string,
  filters: ActiveGraphFilter[]
): Promise<UnionResult> {
  return fetchUnionRaw(caseId, filters)
}
